import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineDocumentText,
  HiOutlineUserGroup,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineBriefcase,
  HiOutlineCog,
  HiOutlineCheckBadge,
  HiOutlineChevronDown,
} from "react-icons/hi2";
import { GroupStatus, Responding } from "types/assessmentTypes";
import { useScope } from "hooks/useScope";

interface RespondersSectionProps {
  activeRespondings: Responding[];
}

export default function RespondersSection({
  activeRespondings,
}: RespondersSectionProps) {
  const { t } = useTranslation();
  const scope = useScope();
  const [selectedResponding, setSelectedResponding] =
    useState<Responding | null>(
      activeRespondings.length > 0 ? activeRespondings[0] : null
    );

  const [expandedCriteria, setExpandedCriteria] = useState<Set<string>>(
    new Set()
  );

  // Group responders by group
  const groupedRespondings = activeRespondings.reduce<Responding[][]>(
    (acc, responding) => {
      const groupId = responding.groupId;
      const existingGroup = acc.find((g) => g[0]?.groupId === groupId);
      if (existingGroup) {
        existingGroup.push(responding);
      } else {
        acc.push([responding]);
      }
      return acc;
    },
    []
  );

  // Get status color and icon
  const getStatusInfo = (responding: Responding) => {
    const hasAnswers = responding.isAnswered;
    const isActive = responding.status === "ACTIVE";

    if (!isActive) {
      return {
        color: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
        borderColor: "border-gray-200 dark:border-gray-700",
        icon: HiOutlineClock,
        label: t("group.statusOptions.DEACTIVE"),
      };
    }

    if (hasAnswers) {
      return {
        color:
          "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        borderColor: "border-green-200 dark:border-green-800",
        icon: HiOutlineCheckCircle,
        label: t("assessment.statuses.COMPLETED"),
      };
    }

    return {
      color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      borderColor: "border-blue-200 dark:border-blue-800",
      icon: HiOutlineClock,
      label: t("assessment.statuses.PENDING"),
    };
  };

  // Group answers by criteria
  const groupAnswersByCriteria = (answers: any[]) => {
    const grouped = new Map<string, any[]>();

    answers.forEach((answer) => {
      const criteria = answer.question?.criteria;
      const criteriaKey = criteria?.index || t("assessment.uncategorized");

      if (!grouped.has(criteriaKey)) {
        grouped.set(criteriaKey, []);
      }
      grouped.get(criteriaKey)!.push(answer);
    });

    return grouped;
  };

  // Toggle criteria expansion
  const toggleCriteria = (criteria: string) => {
    setExpandedCriteria((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(criteria)) {
        newSet.delete(criteria);
      } else {
        newSet.add(criteria);
      }
      return newSet;
    });
  };

  if (activeRespondings.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl bg-white p-12 text-center shadow-sm dark:bg-navy-800"
      >
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700">
          <HiOutlineUserGroup className="h-10 w-10 text-gray-400" />
        </div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">
          {t("assessment.detail.responders.empty")}
        </p>
      </motion.div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      {/* Left Sidebar - Responders List */}
      <div className="lg:col-span-4">
        <div className="sticky top-6 max-h-[80vh] overflow-hidden rounded-2xl bg-white shadow-lg dark:bg-navy-800">
          {/* Header */}
          <div className="border-b border-gray-100 p-4 dark:border-navy-700">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-navy-700 dark:text-white">
                {t("assessment.detail.responders.title")}
              </h3>
              <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-600 dark:bg-brand-900/30 dark:text-brand-200">
                {activeRespondings.length}
              </span>
            </div>
          </div>

          {/* Scrollable List */}
          <div className="custom-scrollbar max-h-[calc(100vh-20rem)] overflow-y-auto p-3">
            <div className="space-y-4">
              {groupedRespondings.map((groupRespondings) => {
                const group = groupRespondings[0]?.group;
                if (!group) return null;

                return (
                  <div key={group.id} className="space-y-1">
                    {/* Group Header */}
                    <div className="flex items-center justify-between px-2 py-1">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-400">
                        {t(`assessmentGroups.${group.key}`)}
                      </p>
                    </div>

                    {/* Responders */}
                    {groupRespondings
                      .filter((r) => r.status === GroupStatus.ACTIVE)
                      .map((responding) => {
                        const isSelected =
                          selectedResponding?.id === responding.id;
                        const hasAnswers = responding.isAnswered;
                        const statusInfo = getStatusInfo(responding);
                        const StatusIcon = statusInfo.icon;

                        return (
                          <motion.button
                            id={`responder-${responding.id}`}
                            key={responding.id}
                            onClick={() => setSelectedResponding(responding)}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            className={`group relative w-full rounded-xl p-3 text-start transition-all duration-200 ${
                              isSelected
                                ? "to-transparent bg-gradient-to-r from-brand-50 ring-1 ring-brand-200 dark:from-brand-900/20 dark:ring-brand-800"
                                : "hover:bg-gray-50 dark:hover:bg-navy-700"
                            }`}
                          >
                            {isSelected && (
                              <div className="absolute right-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-full bg-brand-500 shadow-lg shadow-brand-500/30" />
                            )}

                            <div className="flex items-start gap-3">
                              {/* Avatar */}
                              <div
                                className={`relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold shadow-sm transition-all ${
                                  hasAnswers
                                    ? "bg-gradient-to-br from-green-400 to-green-600 text-white shadow-green-200 dark:shadow-green-900/30"
                                    : "bg-gradient-to-br from-gray-300 to-gray-400 text-white dark:from-gray-600 dark:to-gray-700"
                                }`}
                              >
                                {responding.responder?.name?.charAt(0) || "?"}
                                {hasAnswers && (
                                  <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-0.5 dark:bg-navy-800">
                                    <HiOutlineCheckBadge className="h-3 w-3 text-green-500" />
                                  </span>
                                )}
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="truncate text-sm font-medium text-navy-700 dark:text-white">
                                    {responding.responder?.name ||
                                      responding.responder?.nationalCode ||
                                      "-"}
                                  </span>
                                  <span
                                    className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusInfo.color}`}
                                  >
                                    <StatusIcon className="h-2.5 w-2.5" />
                                    {statusInfo.label}
                                  </span>
                                </div>

                                <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-700 dark:text-gray-400">
                                  <HiOutlineBriefcase className="h-3 w-3" />
                                  <span className="truncate">
                                    {responding.responder?.nationalCode || "-"}
                                  </span>
                                  {hasAnswers && (
                                    <>
                                      <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                                      <span className="text-green-500 dark:text-green-400">
                                        {responding.answers.length}{" "}
                                        {t("assessment.detail.response")}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer - Progress */}
          <div className="border-t border-gray-100 p-4 dark:border-navy-700">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  {t("assessment.detail.respondingPercent")}
                </span>
                <span className="font-medium text-navy-700 dark:text-white">
                  {activeRespondings.filter((r) => r.isAnswered).length} /{" "}
                  {activeRespondings.length}
                </span>
              </div>
              <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-600">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-600"
                  initial={{ width: 0 }}
                  animate={{
                    width: `${
                      (activeRespondings.filter((r) => r.isAnswered).length /
                        activeRespondings.length) *
                      100
                    }%`,
                  }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Responder Details */}
      <div className="lg:col-span-8">
        <AnimatePresence mode="wait">
          {selectedResponding ? (
            <motion.div
              key={selectedResponding.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="rounded-2xl bg-white shadow-lg dark:bg-navy-800"
            >
              {/* Header with Navigation */}
              <div className="border-b border-gray-100 p-5 dark:border-navy-700">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {/* Large Avatar */}
                    <motion.div
                      initial={{ scale: 0.8, rotate: -10 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl text-xl font-bold text-white shadow-lg ${
                        selectedResponding.isAnswered
                          ? "bg-gradient-to-br from-green-400 to-green-600 shadow-green-200 dark:shadow-green-900/30"
                          : "bg-gradient-to-br from-gray-400 to-gray-500 shadow-gray-200 dark:shadow-gray-900/30"
                      }`}
                    >
                      {selectedResponding.responder.name?.charAt(0) || "?"}
                    </motion.div>

                    <div>
                      <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                        {selectedResponding.responder?.name ||
                          selectedResponding.responder?.nationalCode ||
                          "-"}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-700 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <HiOutlineBriefcase className="h-3.5 w-3.5" />
                          {selectedResponding.responder?.nationalCode || "-"}
                        </span>
                        <span className="hidden h-3 w-px bg-gray-300 dark:bg-gray-600 sm:inline" />
                        <span className="flex items-center gap-1">
                          <HiOutlineCog className="h-3.5 w-3.5" />
                          {t(
                            `assessmentGroups.${selectedResponding.group?.key}`
                          )}
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-gray-700 dark:text-gray-500">
                        {selectedResponding.responder?.email && (
                          <>
                            <span className="flex items-center gap-1">
                              <HiOutlineEnvelope className="h-3 w-3" />
                              {selectedResponding.responder?.email}
                            </span>
                            <span className="hidden h-3 w-px bg-gray-300 dark:bg-gray-600 sm:inline" />
                          </>
                        )}
                        <span className="flex items-center gap-1">
                          <HiOutlinePhone className="h-3 w-3" />
                          {selectedResponding.responder.mobile}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Answers - Grouped by Criteria with Accordion */}
              <div className="p-5">
                {selectedResponding.isAnswered ? (
                  <div className="space-y-4">
                    {(() => {
                      const groupedAnswers = groupAnswersByCriteria(
                        selectedResponding.answers
                      );
                      const criteriaEntries = Array.from(
                        groupedAnswers.entries()
                      );

                      return criteriaEntries.map(
                        ([criteria, answers], index) => {
                          const isExpanded = expandedCriteria.has(criteria);
                          const answerCount = answers.length;

                          return (
                            <motion.div
                              key={criteria}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50/30 dark:border-navy-700 dark:bg-navy-700/30"
                            >
                              {/* Criteria Header - Accordion Trigger */}
                              <button
                                onClick={() => {
                                  scope === "admin" && toggleCriteria(criteria);
                                }}
                                className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-gray-100/50 dark:hover:bg-navy-700/50"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-200">
                                    <span className="text-sm font-bold">
                                      {index + 1}
                                    </span>
                                  </div>
                                  <div className="text-start">
                                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                                      {t(`criteria.${criteria}`)}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {answerCount}{" "}
                                      {t("assessment.detail.response")}
                                    </p>
                                  </div>
                                </div>
                                {scope === "admin" && (
                                  <motion.div
                                    animate={{ rotate: isExpanded ? 180 : 0 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <HiOutlineChevronDown className="h-5 w-5 text-gray-400" />
                                  </motion.div>
                                )}
                              </button>

                              {/* Criteria Content - Collapsible */}
                              <AnimatePresence initial={false}>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{
                                      duration: 0.3,
                                      ease: "easeInOut",
                                    }}
                                    className="overflow-hidden"
                                  >
                                    <div className="space-y-3 border-t border-gray-100 p-4 dark:border-navy-700">
                                      {answers.map((answer, answerIdx) => (
                                        <motion.div
                                          key={answer.id}
                                          initial={{ opacity: 0, x: -10 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          transition={{
                                            delay: answerIdx * 0.03,
                                          }}
                                          className="rounded-lg border border-gray-100 bg-white p-4 transition-all hover:border-brand-200 dark:border-navy-600 dark:bg-navy-800 dark:hover:border-brand-800"
                                        >
                                          <div className="flex items-start gap-3">
                                            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-medium text-gray-500 dark:bg-navy-600 dark:text-gray-300">
                                              {answerIdx + 1}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                                {t(
                                                  `question.${answer.question?.index}`
                                                )}
                                              </p>
                                              <div className="mt-1.5 flex flex-wrap items-center gap-3">
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-200">
                                                  <span className="text-gray-400 dark:text-gray-500">
                                                    {t(
                                                      "assessment.detail.response"
                                                    )}
                                                    :
                                                  </span>
                                                  {t(
                                                    `option.${answer.option?.key}`
                                                  )}
                                                </span>
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-200">
                                                  <span className="text-gray-400 dark:text-gray-500">
                                                    {t("assessment.finalScore")}{" "}
                                                    :
                                                  </span>
                                                  {answer.option?.value}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          );
                        }
                      );
                    })()}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="py-16 text-center"
                  >
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700">
                      <HiOutlineDocumentText className="h-10 w-10 text-gray-400" />
                    </div>
                    <p className="mt-4 text-gray-500 dark:text-gray-400">
                      {t("assessment.detail.responders.noAnswers")}
                    </p>
                  </motion.div>
                )}

                {/* Footer Stats */}
                {selectedResponding.isAnswered && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-green-50 to-green-50 p-4 dark:from-green-900/20 dark:to-green-900/20"
                  >
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                        <HiOutlineCheckCircle className="h-5 w-5" />
                        {t("assessment.detail.responders.answersCount")} :{" "}
                        <span className="font-bold">
                          {selectedResponding.answers.length}
                        </span>
                      </span>
                      <span className="text-sm text-gray-800 dark:text-gray-400">
                        {t("assessment.detail.responders.criteriaCount")} :{" "}
                        <span className="font-bold text-navy-700 dark:text-white">
                          {
                            groupAnswersByCriteria(selectedResponding.answers)
                              .size
                          }
                        </span>
                      </span>
                    </div>
                    <span className="rounded-full bg-green-200/50 px-3 py-1 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {t("assessment.detail.stats.completed")}
                    </span>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex h-96 items-center justify-center rounded-2xl bg-white shadow-lg dark:bg-navy-800"
            >
              <div className="text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700">
                  <HiOutlineUserGroup className="h-10 w-10 text-gray-400" />
                </div>
                <p className="mt-4 text-gray-500 dark:text-gray-400">
                  {t("assessment.detail.responders.select")}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
